/**
 * Church tree (F1): full management — create root (org admin) or child
 * (church:write on the parent), navigate to detail. Shows EXACTLY the
 * sub-tree the account can see (buckets parameterized by permissions).
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { buildChurchForest, type ChurchTreeNode, getChurchesQuery, queryKeys } from "@agape/shared";
import { type ChurchRecord, church as churchTable, organization } from "@agape/powersync";
import { Church as ChurchIcon, Plus } from "@fleet/icons";

import { Link } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { Field, FormDialog } from "./form";
import { newId, useDomainQuery, useGrants, useWriter } from "../use-domain";

type ChurchRow = ChurchRecord & { parent_id: string | null; path: string; name: string };

function ChurchNode(props: { node: ChurchTreeNode<ChurchRow> }) {
  return (
    <>
      <Link to="/churches/$churchId" params={{ churchId: props.node.row.id }} class="block">
        <Card
          data-testid="church-item"
          data-church-id={props.node.row.id}
          class="transition-colors hover:border-primary/40"
          style={{ "margin-inline-start": `${props.node.depth * 1.25}rem` }}
        >
          <CardContent class="flex items-center gap-3 py-3">
            <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
              <ChurchIcon size={16} />
            </span>
            <div class="min-w-0">
              <p class="truncate font-medium">{props.node.row.name}</p>
              <Show when={props.node.row.description}>
                <p class="truncate text-sm text-muted-foreground">{props.node.row.description}</p>
              </Show>
            </div>
          </CardContent>
        </Card>
      </Link>
      <For each={props.node.children}>{(child) => <ChurchNode node={child} />}</For>
    </>
  );
}

export function ChurchesPage() {
  const { t } = useI18n();
  const write = useWriter();
  const grants = useGrants();

  const churches = useDomainQuery<ChurchRecord>(
    (uid) => queryKeys.domain.churches(uid),
    (db) => getChurchesQuery(db),
  );
  const orgs = useDomainQuery<{ id: string; name: string | null }>(
    (uid) => ["domain", "organizations", uid],
    (db) => db.select({ id: organization.id, name: organization.name }).from(organization),
  );

  const forest = createMemo(() =>
    buildChurchForest<ChurchRow>(
      (churches.data ?? []).map((row) => ({
        ...row,
        parent_id: row.parent_id,
        path: row.path ?? row.id,
        name: row.name ?? "",
      })),
    ),
  );

  const [open, setOpen] = createSignal(false);
  const [name, setName] = createSignal("");
  const [description, setDescription] = createSignal("");
  const [parentId, setParentId] = createSignal<string>("");
  const [orgId, setOrgId] = createSignal<string>("");

  const parentOptions = createMemo(() => [
    { value: "", label: t(msg`None — root church`) },
    ...(churches.data ?? []).map((c) => ({ value: c.id, label: c.name ?? c.id })),
  ]);
  const orgOptions = createMemo(() =>
    (orgs.data ?? []).map((o) => ({ value: o.id, label: o.name ?? o.id })),
  );

  const create = async () => {
    if (!name().trim()) {
      return;
    }
    const parent = parentId() || null;
    const org = orgId() || orgs.data?.[0]?.id;
    if (!parent && !org) {
      showToast(t(msg`Join an organization first`), "error");
      return;
    }
    // The server computes `path`; the timezone inherits the default and is edited
    // in the detail view. The write-path validates church:write/org-admin.
    await write()
      .insert(churchTable)
      .values({
        id: newId(),
        organization_id: parent ? null : org,
        parent_id: parent,
        path: "",
        name: name().trim(),
        description: description().trim() || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    setOpen(false);
    setName("");
    setDescription("");
    showToast(t(msg`Church created`), "success");
  };

  return (
    <div class="space-y-4">
      <PageSubHeader title={t(msg`Churches`)} />
      <div class="flex items-center justify-between gap-3">
        <p class="text-sm text-muted-foreground">
          {t(msg`The congregations your account can see, as a tree.`)}
        </p>
        <Button size="sm" data-testid="church-create" onClick={() => setOpen(true)}>
          <Plus size={16} /> {t(msg`New church`)}
        </Button>
      </div>
      <Show
        when={forest().length > 0}
        fallback={
          <p data-testid="churches-empty" class="text-muted-foreground">
            {t(msg`No churches synced to this account yet.`)}
          </p>
        }
      >
        <div class="space-y-2" data-testid="churches-tree">
          <For each={forest()}>{(node) => <ChurchNode node={node} />}</For>
        </div>
      </Show>

      <FormDialog
        open={open()}
        onOpenChange={setOpen}
        title={t(msg`New church`)}
        icon={<ChurchIcon size={20} />}
        submitTestId="church-create-submit"
        disabled={!name().trim()}
        onSubmit={create}
      >
        <Field
          id="church-name"
          label={t(msg`Name`)}
          value={name()}
          onInput={setName}
          testId="church-name-input"
        />
        <Field
          id="church-description"
          label={t(msg`Description`)}
          value={description()}
          onInput={setDescription}
        />
        <Field id="church-parent" label={t(msg`Parent church`)}>
          <Select
            options={parentOptions()}
            value={parentId()}
            onChange={setParentId}
            aria-label={t(msg`Parent church`)}
          />
        </Field>
        <Show when={!parentId() && orgOptions().length > 1}>
          <Field id="church-org" label={t(msg`Organization`)}>
            <Select
              options={orgOptions()}
              value={orgId() || orgOptions()[0]?.value}
              onChange={setOrgId}
              aria-label={t(msg`Organization`)}
            />
          </Field>
        </Show>
        <Show when={!parentId() && grants.effective().length === 0}>
          <p class="text-xs text-muted-foreground">
            {t(msg`Root churches can only be created by organization admins.`)}
          </p>
        </Show>
      </FormDialog>
    </div>
  );
}
